import { inject, Injectable } from '@angular/core';
import { UrlResolverService } from '../url/url-resolver.service';
import { EnvironmentService } from '../environment/environment.service';

/** Type representing a downloadable file, which can be a Blob, File, etc. */
export type FileDownloadable = Blob | File | string;

/** The unit steps used when formatting a byte count, from bytes upwards. */
const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/**
 * Service to handle file operations such as downloading, converting or typechecking files
 */
@Injectable({
  providedIn: 'root'
})
export class FileService {
  // Services
  private readonly urlResolver = inject(UrlResolverService);
  private readonly envService = inject(EnvironmentService);

  // Methods
  /**
   * Formats a byte count for display, e.g. `10485760` becomes `10 MB`.
   * Mirrors the wording of the CS3 upload control so both frontends name the same limit the same way.
   * @param bytes The size in bytes. Negative values are treated as 0.
   * @returns The size with the largest unit that keeps the number at or above 1.
   */
  formatFileSize(bytes: number): string {
    let size = Math.max(bytes, 0);
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < FILE_SIZE_UNITS.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    // Whole numbers stay whole ("10 MB"), everything else keeps one decimal ("5.9 MB")
    const rounded = Math.round(size * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} ${FILE_SIZE_UNITS[unitIndex]}`;
  }

  /**
   * Resolves a backend file path to an absolute URL. Relative paths (starting with `/`) are prefixed
   * with `BACKEND_URL` so the file is fetched from the CMS; absolute URLs (the backend already included
   * the host) are passed through unchanged.
   * @param path Relative backend path or absolute URL
   * @returns Absolute URL, or undefined if no path was given
   */
  resolveBackendUrl(path?: string): string | undefined {
    const trimmed = path?.trim();
    if (!trimmed) return undefined;
    return trimmed.startsWith('/') ? `${this.envService.env.BACKEND_URL}${trimmed}` : trimmed;
  }

  /**
   * Converts a Base64 string to a Blob object.
   * @param base64Data Base64 encoded string
   * @param mimeType MIME type of the data (e.g. 'application/pdf')
   * @returns Blob object representing the file data
   */
  convertBase64ToBlob(base64Data: string, mimeType: string): Blob {
    const byteArray = Uint8Array.from(atob(base64Data), (char) => char.charCodeAt(0));
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * Downloads a file given as Blob, File, base64 string, data URI, or URL/relative backend path.
   * Relative paths (starting with '/') and absolute URLs are resolved against BACKEND_URL and
   * downloaded via an anchor; base64 / base64 data URIs are decoded to a Blob.
   * @param file to download
   * @param fileName to save the file as
   * @param mimeType Optional MIME type, used when file is a base64 string without its own type
   * @returns True if download initiated successfully, false otherwise
   */
  downloadFile(file: FileDownloadable, fileName?: string, mimeType?: string): boolean {
    let blob: Blob | undefined;

    if (typeof file === 'string') {
      const value = file.trim();
      // Data URI with base64 encoding → due to size restrictions in browsers on anchor hrefs
      if (value.startsWith('data:')) {
        const dataBlob = this.dataUriToBlob(value, mimeType);
        if (dataBlob) {
          blob = dataBlob;
        } else {
          // Non-base64 data URI — hand the raw URI to the anchor.
          this.downloadFileByUrl(value, fileName);
          return true;
        }
      } else if (value.startsWith('/') || this.isStringUrl(value)) {
        // Relative backend path or absolute URL → resolve against BACKEND_URL and download by URL.
        this.downloadFileByUrl(this.urlResolver.resolveUrl(value, 'backend') ?? value, fileName);
        return true;
      } else if (this.isStringBase64(value)) {
        blob = this.convertBase64ToBlob(value, mimeType ?? 'application/octet-stream');
      }
    } else if (file instanceof Blob) {
      blob = file;
    }

    if (!blob) {
      console.error('Invalid file format for download', file);
      return false;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    this.downloadFileByUrl(blobUrl, fileName);

    // Revoke asynchronously after the download has been triggered
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 1000);

    return true;
  }

  /**
   * Decodes a base64 data URI (e.g. `data:application/pdf;base64,...`) to a Blob, using the MIME
   * type embedded in the URI. Returns null for non-data-URI input or data URIs that aren't base64.
   * @param value the raw string
   * @param fallbackMimeType MIME type to use if the data URI carries none
   * @returns the decoded Blob, or null
   */
  private dataUriToBlob(value: string, fallbackMimeType?: string): Blob | null {
    if (!value.startsWith('data:')) return null;
    const commaIndex = value.indexOf(',');
    if (commaIndex === -1) return null;
    const meta = value.slice('data:'.length, commaIndex);
    if (!/;base64/i.test(meta)) return null;
    const mimeType = meta.split(';')[0] || fallbackMimeType || 'application/octet-stream';
    return this.convertBase64ToBlob(value.slice(commaIndex + 1), mimeType);
  }

  /**
   * Downloads a file by creating a temporary anchor element and triggering a click.
   * @param fileUrl to download the file from
   * @param fileName to save the file as (optional, defaults to 'download')
   */
  downloadFileByUrl(fileUrl: string, fileName?: string): void {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName ?? '';
    link.click();
  }

  /**
   * Checks if a given string is a valid URL.
   * @param value The string to check
   * @returns True if the string is a valid URL, false otherwise
   */
  isStringUrl(value: string | undefined | null): boolean {
    if (!value) return false;

    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Reads a File as a Base64 data URL (e.g. "data:image/png;base64,...").
   * @param file The File to read
   * @returns Promise resolving to the Base64 data URL string
   */
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Checks if a given string is a valid Base64 encoded string.
   * @param value The string to check
   * @returns True if the string is valid Base64, false otherwise
   */
  isStringBase64(value: string): boolean {
    if (!value || value.trim() === '') return false;
    // Base64 Strings must have a length that is a multiple of 4
    if (value.length % 4 !== 0) return false;
    // Check for valid Base64 characters
    const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
    if (!base64Regex.test(value)) return false;

    try {
      // Round-trip test: decode and re-encode to verify validity
      const decoded = atob(value);
      const reencoded = btoa(decoded);
      return reencoded.replace(/=+$/, '') === value.replace(/=+$/, '');
    } catch {
      return false;
    }
  }
}
